import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';

const stripHtml = (html) => {
  if (!html) return '';
  const d = document.createElement('div');
  d.innerHTML = html;
  return d.textContent || '';
};

const OptionLabel = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

const QuestionDetailPage = () => {
  const { slug } = useParams();
  const [question, setQuestion] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);

  useEffect(() => {
    setLoading(true);
    setShowAnswer(false);
    setError(false);
    api.get(`/question-bank/by-slug/${slug}`)
      .then(res => {
        if (res.data.success) setQuestion(res.data.data);
        else setError(true);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0f0526] via-[#1a0b2e] to-[#0f0526] flex items-center justify-center">
        <div className="text-gray-400 animate-pulse text-lg">Loading question...</div>
      </div>
    );
  }

  if (error || !question) {
    return (
      <div className="min-h-screen mt-32 bg-gradient-to-br mt-32 from-[#0f0526] via-[#1a0b2e] to-[#0f0526] flex items-center justify-center text-white">
        <div className="text-center">
          <div className="text-6xl mb-4">🔍</div>
          <h1 className="text-2xl font-bold mb-2">Question Not Found</h1>
          <p className="text-gray-400 mb-6">This question may have been removed or the URL is incorrect.</p>
          <Link to="/question-bank" className="px-6 py-3 bg-purple-600 rounded-xl text-white font-semibold hover:bg-purple-700 transition-all">
            ← Browse All Questions
          </Link>
        </div>
      </div>
    );
  }

  const q = question;
  const qText = stripHtml(q.Question);
  const expText = stripHtml(q.explanation || '');
  const correctOption = q.options?.find(o => o.IsCorrect);
  const correctLabel = correctOption ? OptionLabel[q.options.indexOf(correctOption)] : '';
  const correctText = correctOption ? stripHtml(correctOption.OptionValue) : '';

  // SEO meta
  const seoTitle = q.SeoTitle || `${qText.substring(0, 60)} | BESST`;
  const seoDesc = q.MetaDescription || (expText ? expText.substring(0, 160) : `${qText.substring(0, 130)} - Find the answer at BESST.`);
  const seoKeywords = q.MetaKeywords || [q.CourseName, q.TopicName, 'CUET', 'MCQ', 'question answer'].filter(Boolean).join(', ');
  const canonicalUrl = `https://besst.in/questions/${q.SeoSlug || slug}`;

  // Schema.org structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Question",
    "name": qText,
    "text": qText,
    "answerCount": 1,
    "dateCreated": new Date().toISOString().split('T')[0],
    "author": { "@type": "Organization", "name": "BESST" },
    "acceptedAnswer": {
      "@type": "Answer",
      "text": expText || `The correct answer is ${correctLabel}. ${correctText}`,
      "author": { "@type": "Organization", "name": "BESST" }
    }
  };

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <meta name="keywords" content={seoKeywords} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        {q.OgImage && <meta property="og:image" content={q.OgImage} />}
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <div className="min-h-screen mt-20 bg-gradient-to-br from-[#0f0526] via-[#1a0b2e] to-[#0f0526] text-white">
        {/* Breadcrumb */}
        <div className="max-w-4xl mx-auto px-4 pt-6">
          <nav className="text-sm text-gray-400 flex items-center gap-2 flex-wrap">
            <Link to="/" className="hover:text-purple-300">Home</Link>
            <span>›</span>
            <Link to="/question-bank" className="hover:text-purple-300">Question Bank</Link>
            {q.CourseName && (
              <>
                <span>›</span>
                <Link to={`/question-bank?courseId=${q.CourseId}`} className="hover:text-purple-300">{q.CourseName}</Link>
              </>
            )}
            {q.TopicName && (
              <>
                <span>›</span>
                <span className="text-gray-300">{q.TopicName}</span>
              </>
            )}
          </nav>
        </div>

        {/* Main Content */}
        <article className="max-w-4xl mx-auto px-4 py-8" itemScope itemType="https://schema.org/Question">
          {/* Question Card */}
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden mb-6">
            {/* Tags */}
            <div className="flex items-center gap-2 flex-wrap p-5 pb-0">
              {q.CourseName && <span className="bg-purple-600/20 text-purple-300 text-xs px-3 py-1 rounded-full font-semibold">{q.CourseName}</span>}
              {q.TopicName && <span className="bg-blue-600/20 text-blue-300 text-xs px-3 py-1 rounded-full font-semibold">{q.TopicName}</span>}
              {q.Marks > 0 && <span className="bg-yellow-600/20 text-yellow-300 text-xs px-3 py-1 rounded-full font-semibold">{q.Marks} Mark{q.Marks > 1 ? 's' : ''}</span>}
            </div>

            {/* Question text */}
            <div className="p-5 pt-4">
              <h1 className="text-xl md:text-2xl font-bold leading-relaxed" itemProp="name"
                dangerouslySetInnerHTML={{ __html: q.Question }} />
            </div>

            {/* Bilingual */}
            {q.Question_lang && (
              <div className="px-5 pb-4">
                <div className="p-4 bg-white/5 border-l-4 border-purple-500/40 rounded-r-xl text-gray-300 italic"
                  dangerouslySetInnerHTML={{ __html: q.Question_lang }} />
              </div>
            )}

            {/* Options */}
            {q.options && q.options.length > 0 && (
              <div className="px-5 pb-5 space-y-2">
                {q.options.map((opt, i) => {
                  const isCorrect = opt.IsCorrect;
                  const showCorrect = showAnswer && isCorrect;
                  const showWrong = showAnswer && !isCorrect;

                  return (
                    <div key={opt.OptionId}
                      className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${
                        showCorrect
                          ? 'border-green-500/50 bg-green-500/10'
                          : showWrong
                          ? 'border-white/5 bg-white/[0.02] opacity-60'
                          : 'border-white/10 bg-white/5 hover:border-white/20'
                      }`}>
                      <span className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                        showCorrect ? 'bg-green-500 text-white' : 'bg-white/10 text-gray-400'
                      }`}>
                        {OptionLabel[i]}
                      </span>
                      <span className="flex-1" dangerouslySetInnerHTML={{ __html: opt.OptionValue }} />
                      {showCorrect && <span className="text-green-400 text-xl">✓</span>}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Show Answer button */}
            {!showAnswer && (
              <div className="px-5 pb-5">
                <button onClick={() => setShowAnswer(true)}
                  className="w-full py-3.5 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold hover:shadow-lg hover:shadow-purple-500/20 transition-all">
                  🔓 Reveal Answer & Explanation
                </button>
              </div>
            )}
          </div>

          {/* Explanation (shown after reveal) */}
          {showAnswer && q.explanation && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl p-6 mb-6" itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
              <h2 className="text-lg font-bold text-blue-300 mb-3 flex items-center gap-2">💡 Explanation</h2>
              <div className="text-blue-100 leading-relaxed text-base" itemProp="text"
                dangerouslySetInnerHTML={{ __html: q.explanation }} />
            </div>
          )}

          {showAnswer && !q.explanation && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-6 mb-6">
              <h2 className="text-lg font-bold text-green-300 mb-2">✅ Correct Answer: {correctLabel}</h2>
              {correctText && <p className="text-green-100">{correctText}</p>}
            </div>
          )}

          {/* Related Questions */}
          {q.related && q.related.length > 0 && (
            <div className="mt-10">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">📎 Related Questions</h2>
              <div className="space-y-2">
                {q.related.map(r => (
                  <Link key={r.QuesId} to={`/questions/${r.slug}`}
                    className="block bg-white/5 border border-white/10 rounded-xl p-4 hover:border-purple-500/40 hover:bg-white/[0.07] transition-all no-underline">
                    <p className="text-sm text-gray-200 leading-relaxed">
                      {stripHtml(r.Question).substring(0, 150)}{stripHtml(r.Question).length > 150 ? '...' : ''}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Back link */}
          <div className="mt-8 text-center">
            <Link to="/question-bank" className="text-purple-400 hover:text-purple-300 transition-colors">
              ← Back to Question Bank
            </Link>
          </div>
        </article>
      </div>
    </>
  );
};

export default QuestionDetailPage;